// VendorProductList.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Stack,
  useMediaQuery
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import api from "../../api/axios";

function MobileProductCard({ product, onEdit, onDelete, onView }) {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      }}
    >
      <Typography fontWeight={600}>{product.title}</Typography>

      <Typography variant="body2" color="text.secondary">
        {product.category?.name||'-'}
      </Typography>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mt: 1 }}
      >
        <Typography fontWeight={600}>₹{Number(product.price || 0).toLocaleString("en-IN")}</Typography>
        <Typography variant="body2">
          Stock: {product.stock ?? "-"}
        </Typography>
      </Stack>

      <Typography
        variant="caption"
        sx={{
          display: "inline-block",
          mt: 1,
          px: 1,
          py: 0.3,
          borderRadius: 1,
          bgcolor:
            product.status === "published"
              ? "rgba(46,125,50,0.1)"
              : "rgba(237,108,2,0.1)",
          color:
            product.status === "published"
              ? "green"
              : "orange",
        }}
      >
        {product.status}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        {/* <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={onView}
        >
          View
        </Button> */}

        <Button
          size="small"
          startIcon={<EditIcon />}
          sx={{ color: "#ff9900" }}
          onClick={onEdit}
        >
          Edit
        </Button>

        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
        >
          Delete
        </Button>
        <Tooltip
  title={
    product?.status === "published"
      ? "Unlist (Hide from customers)"
      : "Publish (Make visible to customers)"
  }
>
  <IconButton
    size="small"
    onClick={async () => {
      await api.patch(`/products/${product._id}/toggle-visibility`);

      window.location.reload(); // simplest safe fix for mobile
    }}
    sx={{
      color:
        product?.status === "published"
          ? "orange"
          : "green",
    }}
  >
    {product?.status === "published" ? (
      <VisibilityOffIcon />
    ) : (
      <CheckCircleIcon />
    )}
  </IconButton>
</Tooltip>
      </Stack>
    </Paper>
  );
}


export default function VendorProductList() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]); // ✅ FIX
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Delete dialog
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

    const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "all";

  const [type, setType] = useState(initialType);
  /* ======================
     FETCH VENDOR PRODUCTS
     ====================== */
useEffect(() => {
  const fetchProducts = async () => {
    try {
      const res = await api.get(`/products?type=${type}&vendor=true`);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error("Fetch products failed", err);
    }
  };

  fetchProducts();
}, [type]); // 🔥 THIS is the fix

  /* ======================
     DYNAMIC CATEGORY LIST
     ====================== */
  const CATEGORY_LIST = [
  ...new Map(
    products
      .filter((p) => p.category)
      .map((p) => [p.category._id, p.category])
  ).values(),
];

  /* ======================
     FILTERING
     ====================== */
  const filtered = products.filter((p) => {
    const matchesSearch = p.title
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory = categoryFilter
  ? p.category?._id === categoryFilter
  : true;

    return matchesSearch && matchesCategory;
  });

  const isMobile = useMediaQuery("(max-width:900px)");


  /* ======================
     PAGINATION
     ====================== */
  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ======================
     BULK EXCEL UPLOAD (LOCAL PREVIEW ONLY)
     ====================== */
  const handleBulkUpload = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(worksheet);

      const mapped = json.map((r, i) => ({
        _id: `bulk-${Date.now()}-${i}`,
        title: r.title || r.Title || `Untitled ${i + 1}`,
        category: r.category || r.Category || "Uncategorized",
        price: Number(r.price || r.Price || 0),
        stock: Number(r.stock || r.Stock || 0),
        status: "draft",
      }));

      setProducts((prev) => [...mapped, ...prev]);
      alert(`Imported ${mapped.length} products (preview only)`);
    } catch {
      alert("Failed to import Excel file");
    }
  };

  /* ======================
     DELETE PRODUCT
     ====================== */
  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      setProducts((prev) => prev.filter((p) => p._id !== deleteId));
    } catch {
      alert("Failed to delete product");
    } finally {
      setDeleteOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>
        Vendor Dashboard – Products
      </Typography>

      {/* SEARCH / FILTER / ACTIONS */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={3}>
        <TextField
          label="Search products"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
        />

        <Select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {CATEGORY_LIST.map((cat) => (
  <MenuItem key={cat._id} value={cat._id}>
    {cat.name}
  </MenuItem>
))}
        </Select>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            sx={{ background: "#ff9900" }}
            onClick={() => navigate("/vendor/products/new")}
          >
            + Add Product
          </Button>

          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            component="label"
          >
            Upload Excel
            <input
              hidden
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleBulkUpload(f);
                e.target.value = null;
              }}
            />
          </Button>
        </Stack>
      </Stack>

      {/* TABLE */}
      <Paper sx={{ mt: 3, p: isMobile ? 2 : 0 }}>
  {isMobile ? (
    <Stack spacing={2}>
      {paginated.map((p) => (
        <MobileProductCard
          key={p._id}
          product={p}
          onView={() => navigate(`/vendor/product/${p._id}`)}
          onEdit={() => navigate(`/vendor/edit-product/${p._id}`)}
          onDelete={() => {
            setDeleteId(p._id);
            setDeleteOpen(true);
          }}
        />
      ))}

      {filtered.length === 0 && (
        <Typography align="center" sx={{ py: 4 }}>
          No products found
        </Typography>
      )}
    </Stack>
  ) : (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Stock</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell  align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginated.map((p) => (
            <TableRow key={p._id} hover>
              <TableCell>{p.title}</TableCell>
<TableCell>{p.category?.name || "-"}</TableCell>
              <TableCell>₹{Number(p.price || 0).toLocaleString("en-IN")}</TableCell>
              <TableCell>{p.stock ?? "-"}</TableCell>
              <TableCell>{p.status}</TableCell>

              <TableCell align="right">
                {/* <IconButton onClick={() => navigate(`/vendor/product/${p._id}`)}>
                  <VisibilityIcon />
                </IconButton> */}

                <IconButton
                  sx={{ color: "#ff9900" }}
                  onClick={() =>
                    navigate(`/vendor/edit-product/${p._id}`)
                  }
                >
                  <EditIcon />
                </IconButton>

                <IconButton
                  color="error"
                  onClick={() => {
                    setDeleteId(p._id);
                    setDeleteOpen(true);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
  <Tooltip
  title={
    p.status === "published"
      ? "Unlist (Hide from customers)"
      : "Publish (Make visible to customers)"
  }
>
  <IconButton
    onClick={async () => {
      await api.patch(`/products/${p._id}/toggle-visibility`);

      setProducts((prev) =>
        prev.map((prod) =>
          prod._id === p._id
            ? {
                ...prod,
                status:
                  prod.status === "published"
                    ? "unlisted"
                    : "published",
              }
            : prod
        )
      );
    }}
    sx={{
      color:
        p.status === "published"
          ? "orange"
          : "green",
    }}
  >
    {p.status === "published" ? (
      <VisibilityOffIcon />
    ) : (
      <CheckCircleIcon />
    )}
  </IconButton>
</Tooltip>
              </TableCell>
            </TableRow>
          ))}

          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                No products found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </>
  )}
</Paper>


      {/* DELETE CONFIRM */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete product?</DialogTitle>
        <DialogContent>
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}